import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import {
  answerCoop,
  completeCoop,
  coopRoomId,
  getCoop,
  joinCoop,
  leaveCoop,
  startCoop,
} from "@/api/coop";
import { getQuestionSet } from "@/api/question-sets";
import { chatSocket } from "@/api/chat-socket";
import type { CoopParticipant } from "@/api/community-types";
import type { QuestionSetItemRead } from "@/api/library-types";
import { useAuth } from "@/auth/auth-context";
import { buildPlaylist } from "@/practice/playlist";
import { grade, hasAnswer, isSelfGraded } from "@/practice/grading";
import { questionRendererFor } from "@/practice/renderers/registry";
import { Alert, Button, Spinner } from "@/components/ui";
import { useTranslation } from "react-i18next";

export function CoopSessionPage() {
  const { t } = useTranslation();
  const { coopId = "" } = useParams();
  const { user } = useAuth();
  const myId = user?.id;

  const coop = useQuery({ queryKey: ["coop", coopId], queryFn: () => getCoop(coopId), enabled: !!coopId });
  const session = coop.data;

  // Live sync: any coop envelope on the room → re-fetch authoritative state.
  useEffect(() => {
    if (!coopId) return;
    chatSocket.connect();
    chatSocket.subscribe(coopRoomId(coopId));
    const off = chatSocket.onFrame((frame) => {
      const ft = frame.type;
      if (ft && ft !== "message" && ft !== "subscribed" && ft !== "unsubscribed" && ft !== "error") {
        void coop.refetch();
      }
    });
    return () => {
      off();
      chatSocket.unsubscribe(coopRoomId(coopId));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coopId]);

  const set = useQuery({
    queryKey: ["question-set", session?.question_set_id],
    queryFn: () => getQuestionSet(session!.question_set_id),
    enabled: !!session?.question_set_id,
  });
  const items = useMemo(() => (set.data ? buildPlaylist(set.data).map((e) => e.item) : []), [set.data]);

  const joinMut = useMutation({ mutationFn: () => joinCoop(coopId), onSuccess: () => coop.refetch() });
  const leaveMut = useMutation({ mutationFn: () => leaveCoop(coopId), onSuccess: () => coop.refetch() });
  const startMut = useMutation({ mutationFn: () => startCoop(coopId), onSuccess: () => coop.refetch() });
  const completeMut = useMutation({ mutationFn: () => completeCoop(coopId), onSuccess: () => coop.refetch() });

  const answeredIdx = useRef(-1);
  const answerMut = useMutation({
    mutationFn: (v: { idx: number; correct: boolean }) =>
      answerCoop(coopId, { questionIndex: v.idx, correct: v.correct }),
    onSuccess: () => coop.refetch(),
  });

  if (coop.isLoading) {
    return (
      <div className="flex justify-center py-24 text-brand-600">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }
  if (coop.isError || !session) {
    return (
      <div className="mx-auto max-w-lg">
        <Alert>{coop.error instanceof Error ? coop.error.message : t("Co-op session not found.")}</Alert>
        <Link to="/app/community" className="mt-4 inline-block text-sm text-brand-600 hover:underline">
          ← {t("Community")}
        </Link>
      </div>
    );
  }

  const isHost = session.host_user_id === myId;
  const joined = session.participants.some((p) => p.user_id === myId);
  const idx = session.current_question_index;
  const item: QuestionSetItemRead | undefined = items[idx];

  return (
    <div className="mx-auto max-w-2xl animate-fade-up">
      <Link to="/app/community" className="text-sm text-brand-600 hover:underline">
        ← {t("Community")}
      </Link>
      <h1 className="mt-3 text-2xl font-bold tracking-tight">{t("Co-op session")}</h1>
      <p className="mt-0.5 text-sm text-ink-faint capitalize">{session.status.replace("_", " ")}</p>

      {/* LOBBY */}
      {session.status === "lobby" && (
        <div className="mt-6 space-y-4">
          <Participants list={session.participants} hostId={session.host_user_id} />
          <div className="flex flex-wrap gap-2">
            {!joined && (
              <Button onClick={() => joinMut.mutate()} loading={joinMut.isPending}>
                {t("Join")}
              </Button>
            )}
            {joined && !isHost && (
              <Button variant="secondary" onClick={() => leaveMut.mutate()} loading={leaveMut.isPending}>
                {t("Leave")}
              </Button>
            )}
            {isHost && (
              <Button onClick={() => startMut.mutate()} loading={startMut.isPending}>
                {t("Start for everyone")}
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={() => navigator.clipboard?.writeText(window.location.href)}
            >
              {t("Copy invite link")}
            </Button>
          </div>
          <p className="text-xs text-ink-faint">{t("Share the invite link with classmates so they can join.")}</p>
        </div>
      )}

      {/* IN PROGRESS */}
      {session.status === "in_progress" && (
        <div className="mt-6">
          <div className="mb-4 flex items-center justify-between text-sm text-ink-faint">
            <span>
              {t("Question {{n}} / {{total}}", {
                n: Math.min(idx + 1, items.length),
                total: items.length || "…",
              })}
            </span>
            {isHost && (
              <Button size="sm" variant="ghost" onClick={() => completeMut.mutate()} loading={completeMut.isPending}>
                {t("End session")}
              </Button>
            )}
          </div>
          {!set.data || !item ? (
            <div className="flex justify-center py-10 text-brand-600">
              <Spinner className="h-6 w-6" />
            </div>
          ) : answeredIdx.current >= idx ? (
            <div className="card p-6 text-center text-ink-faint">
              {t("Answer locked in — waiting for the group to advance…")}
            </div>
          ) : (
            <CoopQuestion
              key={`${idx}-${item.id}`}
              item={item}
              onAnswer={(correct) => {
                answeredIdx.current = idx;
                answerMut.mutate({ idx, correct });
              }}
            />
          )}
          <div className="mt-5">
            <Participants list={session.participants} hostId={session.host_user_id} />
          </div>
        </div>
      )}

      {/* COMPLETE */}
      {(session.status === "complete" || session.status === "abandoned") && (
        <div className="mt-6">
          <h2 className="text-lg font-semibold">{t("Final leaderboard")}</h2>
          <div className="card mt-3 divide-y divide-black/5">
            {[...session.participants]
              .sort((a, b) => (Number(b.final_score) || 0) - (Number(a.final_score) || 0))
              .map((p, i) => (
                <div key={p.user_id} className="flex items-center gap-3 px-4 py-2.5">
                  <span className="w-5 text-center font-bold text-ink-faint">{i + 1}</span>
                  <span className="flex-1 truncate text-ink">{p.display_name}</span>
                  <span className="text-sm text-ink-faint">
                    {p.final_score != null && t("{{count}} pts", { count: Math.round(Number(p.final_score)) })}
                    {p.honor_earned > 0 && ` · ${t("+{{count}} honor", { count: p.honor_earned })}`}
                  </span>
                </div>
              ))}
          </div>
          <Link to="/app/community" className="mt-5 inline-block">
            <Button variant="secondary">{t("Back to Community")}</Button>
          </Link>
        </div>
      )}
    </div>
  );
}

function Participants({ list, hostId }: { list: CoopParticipant[]; hostId: string }) {
  const { t } = useTranslation();
  return (
    <div>
      <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-ink-faint">
        {t("Players ({{count}})", { count: list.length })}
      </h3>
      <div className="flex flex-wrap gap-2">
        {list.map((p) => (
          <span
            key={p.user_id}
            className="inline-flex items-center gap-1.5 rounded-full bg-brand-50 px-3 py-1 text-sm text-brand-800"
          >
            {p.display_name}
            {p.user_id === hostId && <span className="text-[10px] font-bold uppercase text-brand-500">{t("host")}</span>}
          </span>
        ))}
      </div>
    </div>
  );
}

/** One co-op question: render, check, report correctness up. Mirrors the solo
 *  Player loop but reports a single boolean instead of running telemetry. */
function CoopQuestion({ item, onAnswer }: { item: QuestionSetItemRead; onAnswer: (correct: boolean) => void }) {
  const { t } = useTranslation();
  const type = item.question_type;
  const content = item.content;
  const [answer, setAnswer] = useState<unknown>(undefined);
  const [revealed, setRevealed] = useState(false);
  const [correct, setCorrect] = useState<boolean | null>(null);
  const Renderer = questionRendererFor(type);

  const onCheck = () => {
    setRevealed(true);
    if (!isSelfGraded(type)) {
      const c = grade(type, content, answer);
      setCorrect(c);
      onAnswer(c);
    }
  };

  return (
    <div>
      <div className="card p-5 sm:p-6">
        <Renderer
          type={type}
          content={content}
          answer={answer}
          setAnswer={setAnswer}
          revealed={revealed}
          correct={correct}
        />
      </div>
      <div className="mt-4 flex justify-end gap-3">
        {!revealed && (
          <Button size="lg" disabled={!hasAnswer(type, content, answer)} onClick={onCheck}>
            {t("Submit")}
          </Button>
        )}
        {revealed && correct === null && (
          <>
            <span className="mr-auto text-sm text-ink-faint">{t("How did you do?")}</span>
            <Button variant="secondary" size="lg" onClick={() => onAnswer(false)}>
              {t("Missed it")}
            </Button>
            <Button size="lg" onClick={() => onAnswer(true)}>
              {t("Got it")}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
